import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { launchImageLibrary } from 'react-native-image-picker';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectSortedEvents } from '@/store/selectors/residentsEventsSelectors';
import { fetchHomeData } from '@/store/slices/residents/homeSlice';
import { fetchWifiInfo } from '@/store/slices/residents/wifiSlice';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { useGetLeaseQuery } from '@/store/api';
import { useMaintenanceTickets } from '@/hooks/residents/useMaintenanceTickets';
import { useUnreadPropertyChatCount } from '@/hooks/residents/useUnreadPropertyChatCount';
import { useGetThreadsQuery, useGetNewsfeedUnseenCountQuery } from '@/store/api/corkBoard/corkBoardApi';
import { countSurveysAwaitingResponse } from '@/utils/residents/surveys/surveysAwaitingResponse';
import { CommunityResource } from '@/types/residents/communityResource';
import { Event as ResioEvent } from '@/types/residents/event';
import { Bulletin } from '@/types/residents/bulletin';
import { formatCurrency } from '@/utils/common/formatting';

export interface RecentActivityItem {
  id: string;
  type: 'maintenance' | 'bulletin' | 'payment';
  title: string;
  subtitle: string;
  status?: 'in_progress' | 'pending' | 'completed';
  badgeCount?: number;
  timestamp?: string;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function mapPaymentStatus(settlementStatus?: string | null): 'pending' | 'completed' {
  if (!settlementStatus || settlementStatus === 'Pending Finalization') return 'pending';
  const lower = (settlementStatus || '').toLowerCase();
  if (lower.includes('rejected') || lower.includes('declined') || lower.includes('voided') || lower.includes('failed')) {
    return 'pending';
  }
  return 'completed';
}

export interface UseHomeScreenReturn {
  // User & Auth
  user: any;
  
  // Redesign computed
  greeting: string;
  firstName: string;
  unitName: string;
  propertyName: string;
  propertyImageUrl: string | null;
  recentActivity: RecentActivityItem[];
  
  // Data
  resources: CommunityResource[];
  linkedAccounts: any[];
  newsfeedNewCount: number;
  unreadMessagesCount: number;
  validEvents: ResioEvent[];
  surveysToAnswerCount: number;
  wifiInfo: any;
  balanceDue: number;
  autopayEnabled: boolean;
  openTickets: any[];
  
  // Loading states
  isPaymentLoading: boolean;
  isMaintenanceLoading: boolean;
  refreshing: boolean;
  
  // Computed states
  shouldShowPayments: boolean;
  shouldShowEventsSection: boolean;
  shouldShowWiFiCard: boolean;
  
  // Handlers
  handleRefresh: () => Promise<void>;
  handleBulletinPress: () => void;
  handleEventPress: (event: ResioEvent) => void;
  handleEventsListPress: () => void;
  handleSurveysPress: () => void;
  handlePlannerPress: () => void;
  handleWiFiPress: () => void;
  handleResourcePress: (resource?: CommunityResource) => void;
  handleLinkedAccountsPress: () => void;
  handleMarketplacePress: () => void;
  handlePaymentsPress: () => void;
  handleMakePayment: () => void;
  handleMaintenancePress: () => void;
  handleMessagesPress: () => void;
  handleUploadPhoto: () => void;
  handleActivityItemPress: (item: RecentActivityItem) => void;
  handleResourcesPress: () => void;
}

/**
 * useHomeScreen - Shared hook for home screen business logic
 * 
 * Extracts all data fetching, state management, and navigation handlers
 * to be shared between mobile and desktop home screen views.
 */
export const useHomeScreen = (): UseHomeScreenReturn => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { resources, linkedAccounts, bulletins } = useAppSelector((state) => state.home);
  const events = useAppSelector(selectSortedEvents);
  const { wifiInfo } = useAppSelector((state) => state.wifi);
  
  // Refresh state
  const [refreshing, setRefreshing] = useState(false);
  
  // Payment data hook
  const leaseId = user?.currentLeaseId ? String(user.currentLeaseId) : '';
  const { balanceDue, isLoading: isPaymentLoading } = usePaymentBalance(leaseId);

  const leaseIdNum = user?.currentLeaseId || 0;
  const { data: leaseData } = useGetLeaseQuery(
    { leaseId: leaseIdNum, includeTransactions: false, includePayments: true },
    { skip: !leaseIdNum },
  );
  const autopayEnabled = useMemo(() => {
    if (!leaseData) return false;
    const response = (leaseData as any)?.response || leaseData;
    const lease = response?.lease || (response?.lease?.id ? response.lease : response);
    const recurringPayments = lease?.recurringPayments || [];
    return Array.isArray(recurringPayments) && recurringPayments.length > 0;
  }, [leaseData]);

  // Hide Payments for Non-Leasing Occupants
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';

  const recentPayments = useMemo(() => {
    if (!leaseData || !shouldShowPayments) return [];
    const response = (leaseData as any)?.response || leaseData;
    const payments: any[] = response?.payments || [];
    return payments
      .map((p: any) => ({
        id: `pay-${p.id}`,
        date: p.createdAt || p.postedAt || p.postDate || '',
        description: p.arCodeDescription === 'Resio Fee'
          ? 'Resio Convenience Fee'
          : `Payment - ${p.arCodeDescription || p.description || 'Rent'}`,
        amount: Number(p.amount ?? 0),
        status: mapPaymentStatus(p.settlementStatus),
      }))
      .filter((p) => p.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [leaseData, shouldShowPayments]);

  // Maintenance tickets hook
  const { openTickets, loading: isMaintenanceLoading, refresh: refreshMaintenance } = useMaintenanceTickets();

  // Unread property-chat conversations for the Messages quick action. Shares
  // the polled /messages/unread-count cache, which mark-as-read optimistically
  // decrements, so the badge clears as conversations are opened.
  const unreadMessagesCount = useUnreadPropertyChatCount();

  // Newsfeed threads posted since the resident last opened the board. Opening
  // the Community Board stamps the visit (markNewsfeedSeen), which zeroes this
  // optimistically and invalidates the cache.
  const { data: newsfeedNewCount } = useGetNewsfeedUnseenCountQuery(
    { propertyId: user?.propertyId ?? 0 },
    { skip: !user?.propertyId },
  );

  // Surveys awaiting the resident's response for the home Quick Actions card.
  // Open + unanswered from the first page of results — enough for the badge;
  // the Surveys screen owns the full list.
  const { data: surveysData } = useGetThreadsQuery(
    { propertyId: user?.propertyId ?? 0, kind: 'survey', page: 0, pageSize: 50 },
    { skip: !user?.propertyId },
  );
  const surveysToAnswerCount = useMemo(
    () => countSurveysAwaitingResponse(surveysData?.threads),
    [surveysData],
  );
  
  // Redesign computed values
  const greeting = getGreeting();
  const firstName = user?.firstName || '';
  const unitName = user?.unitNumber ? `Unit ${user.unitNumber}` : '';
  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';
  const propertyImageUrl: string | null = user?.property?.photoUrl || null;

  const recentActivity = useMemo<RecentActivityItem[]>(() => {
    const items: RecentActivityItem[] = [];

    // Maintenance tickets
    if (openTickets?.length) {
      openTickets.forEach((ticket: any) => {
        items.push({
          id: `maint-${ticket.id}`,
          type: 'maintenance',
          title: ticket.subject || ticket.title || 'Maintenance request',
          subtitle: ticket.statusDescription || ticket.description || '',
          status: ticket.status === 'in_progress' ? 'in_progress' : 'pending',
          timestamp: ticket.updatedOn || ticket.createdOn || '',
        });
      });
    }

    // Individual unread bulletins
    if (bulletins?.length) {
      bulletins
        .filter((b: Bulletin) => b.messageReadCount === '0')
        .forEach((b: Bulletin) => {
          items.push({
            id: `bulletin-${b.id}`,
            type: 'bulletin',
            title: b.title || 'Community update',
            subtitle: 'New bulletin',
            timestamp: b.updatedAt || b.createdAt || '',
          });
        });
    }

    // Recent payments
    recentPayments.forEach((p) => {
      items.push({
        id: p.id,
        type: 'payment',
        title: p.description,
        subtitle: formatCurrency(Math.abs(p.amount)),
        status: p.status,
        timestamp: p.date,
      });
    });

    // Sort newest first, then take top 3
    items.sort((a, b) => {
      const tA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tB - tA;
    });

    return items.slice(0, 3);
  }, [openTickets, bulletins, recentPayments]);

  // Check for undefined events and filter valid events
  const validEvents = events.filter((event) => event && event.id);
  
  // iOS behavior: Hide sections when empty/unavailable
  const shouldShowEventsSection = validEvents.length > 0;
  const shouldShowWiFiCard = wifiInfo !== null;

  // Fetch home data when component mounts or when user changes
  useEffect(() => {
    if (user?.propertyId) {
      dispatch(fetchHomeData(user.propertyId));
    }
    // Fetch WiFi info if lease ID is available and not already loaded
    if (user?.currentLeaseId && !wifiInfo) {
      dispatch(fetchWifiInfo(user.currentLeaseId));
    }
  }, [dispatch, user?.propertyId, user?.currentLeaseId, wifiInfo]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (user?.propertyId) {
        await dispatch(fetchHomeData(user.propertyId));
      }
      refreshMaintenance();
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, user?.propertyId, refreshMaintenance]);

  // Navigation handlers
  const handleBulletinPress = useCallback(() => {
    navigation.navigate('ResidentCommunityBoard');
  }, [navigation]);

  const handleEventPress = useCallback((event: ResioEvent) => {
    navigation.navigate('EventDetail', { event });
  }, [navigation]);

  const handleEventsListPress = useCallback(() => {
    navigation.navigate('ResidentEvents' as any);
  }, [navigation]);

  const handleSurveysPress = useCallback(() => {
    navigation.navigate('ResidentSurveys' as any, { showBackLink: true });
  }, [navigation]);

  const handlePlannerPress = useCallback(() => {
    navigation.navigate('ResidentPlanner' as any, { showBackLink: true });
  }, [navigation]);

  const handleWiFiPress = useCallback(() => {
    navigation.navigate('WifiConnection');
  }, [navigation]);

  const handleResourcePress = useCallback((resource?: CommunityResource) => {
    if (resource) {
      navigation.navigate('MobileResourceDetail', { resource });
    } else {
      navigation.navigate('ResidentResources');
    }
  }, [navigation]);

  const handleLinkedAccountsPress = useCallback(() => {
    navigation.navigate('ResidentLinkedAccounts');
  }, [navigation]);

  const handleMarketplacePress = useCallback(() => {
    navigation.navigate('ResidentMarketplace');
  }, [navigation]);

  const handlePaymentsPress = useCallback(() => {
    navigation.navigate('ResidentPayments');
  }, [navigation]);

  const handleMakePayment = useCallback(() => {
    if (user?.currentLeaseId) {
      navigation.navigate('MakePayment', { leaseId: user.currentLeaseId });
    } else {
      navigation.navigate('ResidentPayments');
    }
  }, [navigation, user?.currentLeaseId]);

  const handleMaintenancePress = useCallback(() => {
    navigation.navigate('ResidentMaintenance');
  }, [navigation]);

  const handleMessagesPress = useCallback(() => {
    navigation.navigate('ResidentMessages');
  }, [navigation]);

  const handleUploadPhoto = useCallback(() => {
    launchImageLibrary({ mediaType: 'photo', quality: 0.8 }, (_response) => {
      // Upload handling will be wired to API when backend endpoint is available
    });
  }, []);

  const handleActivityItemPress = useCallback((item: RecentActivityItem) => {
    switch (item.type) {
      case 'maintenance':
        navigation.navigate('ResidentMaintenance');
        break;
      case 'payment':
        navigation.navigate('ResidentPayments');
        break;
      case 'bulletin':
        navigation.navigate('ResidentNotifications');
        break;
    }
  }, [navigation]);

  const handleResourcesPress = useCallback(() => {
    navigation.navigate('ResidentResources');
  }, [navigation]);

  return {
    // User & Auth
    user,
    
    // Redesign computed
    greeting,
    firstName,
    unitName,
    propertyName,
    propertyImageUrl,
    recentActivity,
    
    // Data
    resources: resources || [],
    linkedAccounts: linkedAccounts || [],
    newsfeedNewCount: newsfeedNewCount ?? 0,
    unreadMessagesCount,
    validEvents,
    surveysToAnswerCount,
    wifiInfo,
    balanceDue,
    autopayEnabled,
    openTickets: openTickets || [],
    
    // Loading states
    isPaymentLoading,
    isMaintenanceLoading,
    refreshing,
    
    // Computed states
    shouldShowPayments,
    shouldShowEventsSection,
    shouldShowWiFiCard,
    
    // Handlers
    handleRefresh,
    handleBulletinPress,
    handleEventPress,
    handleEventsListPress,
    handleSurveysPress,
    handlePlannerPress,
    handleWiFiPress,
    handleResourcePress,
    handleLinkedAccountsPress,
    handleMarketplacePress,
    handlePaymentsPress,
    handleMakePayment,
    handleMaintenancePress,
    handleMessagesPress,
    handleUploadPhoto,
    handleActivityItemPress,
    handleResourcesPress,
  };
};

export default useHomeScreen;
